/**
 * IFormField - Base interface for data entry form field components
 * 
 * Extends IComponent with specific properties and methods for form fields
 * that support reading/writing values, validation, and change notifications.
 * 
 * All form field components (CheckBox, TextInput, DatePicker, DropDown, etc.)
 * should implement this interface for consistent CRUD form integration.
 */

import { IComponent } from './IComponent';

/**
 * Common sizes for form field components
 */
export type FieldSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Common color variants for form field components
 */
export type FieldColor = 'primary' | 'secondary' | 'accent' | 'neutral' | 'success' | 'warning' | 'info' | 'error';

/**
 * Validation result returned by validate()
 */
export interface ValidationResult {
  valid: boolean;
  message?: string;
}

/**
 * Event fired when a field value changes
 */
export interface FieldChangeEvent<T = any> {
  /** The field that changed */
  source: IFormField<T>;
  /** The field's name/identifier */
  fieldName: string;
  /** The previous value */
  oldValue: T;
  /** The new value */
  newValue: T;
  /** Timestamp of the change */
  timestamp: Date;
}

/**
 * Base options for all form field components
 */
export interface FormFieldOptions {
  /** Unique field name/identifier - used for data binding */
  name: string;
  /** Display label for the field */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is read-only */
  readonly?: boolean;
  /** Size variant */
  size?: FieldSize;
  /** Color variant */
  color?: FieldColor;
  /** CSS classes to add to the component */
  className?: string;
  /** Callback when value changes */
  onChange?: (event: FieldChangeEvent) => void;
}

/**
 * IFormField - Interface for form field components
 * 
 * @template T The type of value the field holds
 */
export interface IFormField<T = any> extends IComponent {
  /**
   * Get the field's unique name/identifier
   */
  getName(): string;

  /**
   * Get the current value of the field
   */
  getValue(): T;

  /**
   * Set the value of the field
   * @param value The new value
   * @param silent If true, don't fire onChange event
   */
  setValue(value: T, silent?: boolean): void;

  /**
   * Get the display label for the field
   */
  getLabel(): string | undefined;

  /**
   * Check if the field is required
   */
  isRequired(): boolean;

  /**
   * Check if the field is disabled
   */
  isDisabled(): boolean;

  /**
   * Set the disabled state
   */
  setDisabled(disabled: boolean): void;

  /**
   * Check if the field is read-only
   */
  isReadOnly(): boolean;

  /**
   * Set the read-only state
   */
  setReadOnly(readonly: boolean): void;

  /**
   * Validate the field's current value
   * @returns Validation result with success flag and optional error message
   */
  validate(): ValidationResult;

  /**
   * Clear the field to its default/empty state
   */
  clear(): void;

  /**
   * Focus the field
   */
  focus(): void;

  /**
   * Register a change handler
   * @param handler Callback function for value changes
   */
  onChange(handler: (event: FieldChangeEvent<T>) => void): void;

  /**
   * Remove a change handler
   * @param handler The handler to remove
   */
  offChange(handler: (event: FieldChangeEvent<T>) => void): void;
}

/**
 * Abstract base class providing common functionality for form fields
 * Extend this class to create specific field types
 */
export abstract class BaseFormField<T = any> implements IFormField<T> {
  protected container: HTMLElement;
  protected options: FormFieldOptions;
  protected changeHandlers: Set<(event: FieldChangeEvent<T>) => void> = new Set();
  protected currentValue: T;
  protected defaultValue: T;

  constructor(container: HTMLElement, options: FormFieldOptions, defaultValue: T) {
    this.container = container;
    this.options = options;
    this.currentValue = defaultValue;
    this.defaultValue = defaultValue;

    // Register initial onChange handler if provided
    if (options.onChange) {
      this.changeHandlers.add(options.onChange);
    }
  }

  abstract initialize(): Promise<void>;
  abstract destroy(): void;
  abstract focus(): void;

  getName(): string {
    return this.options.name;
  }

  getValue(): T {
    return this.currentValue;
  }

  setValue(value: T, silent: boolean = false): void {
    const oldValue = this.currentValue;
    this.currentValue = value;
    this.updateDisplay();

    if (!silent && oldValue !== value) {
      this.fireChangeEvent(oldValue, value);
    }
  }

  getLabel(): string | undefined {
    return this.options.label;
  }

  isRequired(): boolean {
    return this.options.required ?? false;
  }

  isDisabled(): boolean {
    return this.options.disabled ?? false;
  }

  abstract setDisabled(disabled: boolean): void;

  isReadOnly(): boolean {
    return this.options.readonly ?? false;
  }

  abstract setReadOnly(readonly: boolean): void;

  validate(): ValidationResult {
    // Base validation: check required
    if (this.isRequired() && this.isEmpty()) {
      return {
        valid: false,
        message: `${this.options.label || this.options.name} is required`
      };
    }
    return { valid: true };
  }

  clear(): void {
    this.setValue(this.defaultValue);
  }

  onChange(handler: (event: FieldChangeEvent<T>) => void): void {
    this.changeHandlers.add(handler);
  }

  offChange(handler: (event: FieldChangeEvent<T>) => void): void {
    this.changeHandlers.delete(handler);
  }

  /**
   * Check if the field value is empty (override in subclasses as needed)
   */
  protected isEmpty(): boolean {
    return this.currentValue === null || this.currentValue === undefined;
  }

  /**
   * Update the visual display to match the current value
   */
  protected abstract updateDisplay(): void;

  /**
   * Fire change event to all registered handlers
   */
  protected fireChangeEvent(oldValue: T, newValue: T): void {
    const event: FieldChangeEvent<T> = {
      source: this,
      fieldName: this.options.name,
      oldValue,
      newValue,
      timestamp: new Date()
    };

    this.changeHandlers.forEach(handler => {
      try {
        handler(event);
      } catch (error) {
        console.error('Error in change handler:', error);
      }
    });
  }
}
